import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Building2, Plus, Search, Edit, Trash2, Users, MapPin, Phone, Mail,
  RefreshCcw, FileText, Check, X, Link2, Unlink, AlertCircle, Eye,
  Building, Briefcase, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

/** Normalize FastAPI/axios errors into a toast-safe string (never render raw objects). */
const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg))
      .filter(Boolean)
      .join(', ') || fallback;
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

/** Build API payload: empty optional int fields must be null, not "". */
const buildCompanyPayload = (formData) => ({
  ...formData,
  opco_id: formData.opco_id === '' || formData.opco_id == null
    ? null
    : Number(formData.opco_id),
});

const CfaAdminCompaniesPage = ({ onMenuClick }) => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterStudents, setFilterStudents] = useState('all');
  const [filterOpco, setFilterOpco] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [opcos, setOpcos] = useState([]);

  // Form state
  const emptyForm = {
    name: '',
    siret: '',
    opco_id: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_position: '',
    hr_contact_name: '',
    hr_contact_email: '',
    hr_contact_phone: '',
    legal_form: '',
    naf_code: '',
    workforce: '',
    collective_agreement: '',
    notes: ''
  };
  const [formData, setFormData] = useState(emptyForm);

  // Fetch OPCOs
  const fetchOpcos = async () => {
    try {
      const response = await api.get('/companies/opcos/list');
      setOpcos(response.data);
    } catch (error) {
      console.error('Error fetching OPCOs:', error);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterActive !== 'all') params.is_active = filterActive === 'active';
      if (filterStudents !== 'all') params.has_students = filterStudents === 'with';
      if (filterOpco) params.opco_id = filterOpco;
      
      const [companiesRes, statsRes] = await Promise.all([
        api.get('/companies', { params }),
        api.get('/companies/stats')
      ]);
      
      setCompanies(companiesRes.data.companies || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Erreur lors du chargement des entreprises');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterActive, filterStudents, filterOpco]);

  useEffect(() => {
    fetchData();
    fetchOpcos();
  }, [fetchData]);

  // Create company
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Le nom de l\'entreprise est obligatoire');
      return;
    }

    setSaving(true);
    try {
      await api.post('/companies', buildCompanyPayload(formData));
      toast.success('Entreprise créée avec succès');
      setShowCreateModal(false);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    } finally {
      setSaving(false);
    }
  };

  // Update company
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;

    setSaving(true);
    try {
      await api.put(`/companies/${selectedCompany.id}`, buildCompanyPayload(formData));
      toast.success('Entreprise mise à jour avec succès');
      setShowEditModal(false);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error(getApiErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  // Delete company
  const handleDelete = async () => {
    if (!selectedCompany) return;

    setSaving(true);
    try {
      const response = await api.delete(`/companies/${selectedCompany.id}`);
      toast.success(response.data.message);
      setShowDeleteModal(false);
      setSelectedCompany(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting company:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  // Open edit modal
  const openEditModal = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      siret: company.siret || '',
      opco_id: company.opco_id || '',
      address: company.address || '',
      postal_code: company.postal_code || '',
      city: company.city || '',
      country: company.country || 'France',
      contact_name: company.contact_name || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      contact_position: company.contact_position || '',
      hr_contact_name: company.hr_contact_name || '',
      hr_contact_email: company.hr_contact_email || '',
      hr_contact_phone: company.hr_contact_phone || '',
      legal_form: company.legal_form || '',
      naf_code: company.naf_code || '',
      workforce: company.workforce || '',
      collective_agreement: company.collective_agreement || '',
      notes: company.notes || ''
    });
    setShowEditModal(true);
  };

  // Open link students modal
  const openLinkModal = async (company) => {
    setSelectedCompany(company);
    try {
      const response = await api.get(`/companies/${company.id}/available-students`);
      setAvailableStudents(response.data);
      setShowLinkModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des étudiants');
    }
  };

  // Link/unlink student
  const handleToggleStudentLink = async (student) => {
    if (!selectedCompany) return;
    
    try {
      if (student.is_linked) {
        await api.post(`/companies/${selectedCompany.id}/unlink-student`, { student_id: student.id });
        toast.success(`${student.full_name} détaché(e)`);
      } else {
        await api.post(`/companies/${selectedCompany.id}/link-student`, { student_id: student.id });
        toast.success(`${student.full_name} rattaché(e)`);
      }
      
      // Refresh available students
      const response = await api.get(`/companies/${selectedCompany.id}/available-students`);
      setAvailableStudents(response.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  // Legal form options
  const legalForms = [
    { value: '', label: 'Sélectionner' },
    { value: 'SA', label: 'SA - Société Anonyme' },
    { value: 'SARL', label: 'SARL - Société à Responsabilité Limitée' },
    { value: 'SAS', label: 'SAS - Société par Actions Simplifiée' },
    { value: 'SASU', label: 'SASU - SAS Unipersonnelle' },
    { value: 'EURL', label: 'EURL - Entreprise Unipersonnelle à Responsabilité Limitée' },
    { value: 'EI', label: 'EI - Entreprise Individuelle' },
    { value: 'SCOP', label: 'SCOP - Société Coopérative' },
    { value: 'Association', label: 'Association Loi 1901' },
    { value: 'Autre', label: 'Autre' }
  ];

  const workforceOptions = [
    { value: '', label: 'Sélectionner' },
    { value: '1-10', label: '1 à 10 salariés' },
    { value: '11-49', label: '11 à 49 salariés' },
    { value: '50-249', label: '50 à 249 salariés' },
    { value: '250-499', label: '250 à 499 salariés' },
    { value: '500+', label: '500 salariés et plus' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Header title="Entreprises d'accueil" onMenuClick={onMenuClick} />
      
      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card data-testid="stats-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                  <Building2 className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total || 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stats-active">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.active || 0}</p>
                  <p className="text-xs text-slate-500">Actives</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stats-with-students">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.with_students || 0}</p>
                  <p className="text-xs text-slate-500">Avec étudiants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stats-students-linked">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Link2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total_students_linked || 0}</p>
                  <p className="text-xs text-slate-500">Étudiants liés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Toolbar */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-input"
                  />
                </div>
                
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="filter-active"
                >
                  <option value="all">Toutes</option>
                  <option value="active">Actives</option>
                  <option value="inactive">Inactives</option>
                </select>
                
                <select
                  value={filterStudents}
                  onChange={(e) => setFilterStudents(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="filter-students"
                >
                  <option value="all">Tous</option>
                  <option value="with">Avec étudiants</option>
                  <option value="without">Sans étudiants</option>
                </select>
                
                <select
                  value={filterOpco}
                  onChange={(e) => setFilterOpco(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="filter-opco"
                >
                  <option value="">Tous les OPCOs</option>
                  {opcos.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
                <Button onClick={() => { setFormData(emptyForm); setShowCreateModal(true); }} data-testid="add-company-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle entreprise
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Companies List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Liste des entreprises
            </CardTitle>
            <CardDescription>
              {companies.length} entreprise(s) trouvée(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCcw className="w-6 h-6 animate-spin text-violet-600" />
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">Aucune entreprise trouvée</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une entreprise
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Entreprise</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Contact tuteur</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Ville</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Étudiants</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Statut</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => (
                      <tr key={company.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                              <Building className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">{company.name}</p>
                              {company.siret && (
                                <p className="text-xs text-slate-500">SIRET: {company.siret}</p>
                              )}
                              {company.opco && (
                                <p className="text-xs text-violet-600">{company.opco.name}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {company.contact_name ? (
                            <div>
                              <p className="text-sm text-slate-900 dark:text-white">{company.contact_name}</p>
                              {company.contact_email && (
                                <p className="text-xs text-slate-500">{company.contact_email}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">Non renseigné</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {company.city ? (
                            <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                              <MapPin className="w-3 h-3" />
                              {company.city}
                            </div>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            company.student_count > 0 
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                          }`}>
                            <Users className="w-3 h-3" />
                            {company.student_count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            company.is_active
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {company.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                            {company.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => { setSelectedCompany(company); setShowViewModal(true); }}
                              data-testid={`view-company-${company.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openLinkModal(company)}
                              data-testid={`link-students-${company.id}`}
                            >
                              <Link2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditModal(company)}
                              data-testid={`edit-company-${company.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => { setSelectedCompany(company); setShowDeleteModal(true); }}
                              data-testid={`delete-company-${company.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            setShowEditModal(false);
            setSelectedCompany(null);
          }
        }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-600" />
                {showEditModal ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}
              </DialogTitle>
              <DialogDescription>
                {showEditModal ? 'Modifiez les informations de l\'entreprise' : 'Renseignez les informations de l\'entreprise d\'accueil'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-6">
              {/* Informations générales */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Informations générales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'entreprise *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: TechCorp Solutions"
                      required
                      data-testid="company-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SIRET</label>
                    <Input
                      value={formData.siret}
                      onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                      placeholder="123 456 789 00001"
                      data-testid="company-siret"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Forme juridique</label>
                    <select
                      value={formData.legal_form}
                      onChange={(e) => setFormData({ ...formData, legal_form: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300"
                      data-testid="company-legal-form"
                    >
                      {legalForms.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Code NAF/APE</label>
                    <Input
                      value={formData.naf_code}
                      onChange={(e) => setFormData({ ...formData, naf_code: e.target.value })}
                      placeholder="6201Z"
                      data-testid="company-naf"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Effectif</label>
                    <select
                      value={formData.workforce}
                      onChange={(e) => setFormData({ ...formData, workforce: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300"
                      data-testid="company-workforce"
                    >
                      {workforceOptions.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">OPCO</label>
                    <select
                      value={formData.opco_id}
                      onChange={(e) => setFormData({ ...formData, opco_id: e.target.value ? parseInt(e.target.value) : '' })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300"
                      data-testid="company-opco"
                    >
                      <option value="">Sélectionner un OPCO</option>
                      {opcos.map(o => (
                        <option key={o.id} value={o.id}>{o.name} - {o.description}</option>
                      ))}
                    </select>
                    {formData.opco_id && opcos.find(o => o.id === formData.opco_id)?.sectors && (
                      <p className="text-xs text-slate-500 mt-1">
                        Secteurs : {opcos.find(o => o.id === formData.opco_id)?.sectors}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="12 rue de l'Innovation"
                      data-testid="company-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="75008"
                      data-testid="company-postal"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Paris"
                      data-testid="company-city"
                    />
                  </div>
                </div>
              </div>

              {/* Contact tuteur */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Tuteur / Maître d'apprentissage
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet</label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="Jean Dupont"
                      data-testid="company-contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Fonction</label>
                    <Input
                      value={formData.contact_position}
                      onChange={(e) => setFormData({ ...formData, contact_position: e.target.value })}
                      placeholder="Directeur Technique"
                      data-testid="company-contact-position"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="jean.dupont@entreprise.fr"
                      data-testid="company-contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      maxLength={15}
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      placeholder="0123456789"
                      data-testid="company-contact-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Contact RH */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Contact RH (optionnel)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                    <Input
                      value={formData.hr_contact_name}
                      onChange={(e) => setFormData({ ...formData, hr_contact_name: e.target.value })}
                      placeholder="Marie Martin"
                      data-testid="company-hr-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={formData.hr_contact_email}
                      onChange={(e) => setFormData({ ...formData, hr_contact_email: e.target.value })}
                      placeholder="rh@entreprise.fr"
                      data-testid="company-hr-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      maxLength={15}
                      value={formData.hr_contact_phone}
                      onChange={(e) => setFormData({ ...formData, hr_contact_phone: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                      placeholder="0123456790"
                      data-testid="company-hr-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Convention collective */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Convention collective
                </h3>
                <Input
                  value={formData.collective_agreement}
                  onChange={(e) => setFormData({ ...formData, collective_agreement: e.target.value })}
                  placeholder="Ex: Convention collective Syntec"
                  data-testid="company-convention"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes internes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Informations complémentaires..."
                  rows={3}
                  data-testid="company-notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}>
                  Annuler
                </Button>
                <Button type="submit" disabled={saving} data-testid="submit-company">
                  {saving ? (
                    <>
                      <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    showEditModal ? 'Enregistrer' : 'Créer l\'entreprise'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-600" />
                {selectedCompany?.name}
              </DialogTitle>
            </DialogHeader>
            
            {selectedCompany && (
              <div className="space-y-6">
                {/* Info principales */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedCompany.siret && (
                    <div>
                      <p className="text-xs text-slate-500">SIRET</p>
                      <p className="font-medium">{selectedCompany.siret}</p>
                    </div>
                  )}
                  {selectedCompany.legal_form && (
                    <div>
                      <p className="text-xs text-slate-500">Forme juridique</p>
                      <p className="font-medium">{selectedCompany.legal_form}</p>
                    </div>
                  )}
                  {selectedCompany.workforce && (
                    <div>
                      <p className="text-xs text-slate-500">Effectif</p>
                      <p className="font-medium">{selectedCompany.workforce}</p>
                    </div>
                  )}
                  {selectedCompany.city && (
                    <div>
                      <p className="text-xs text-slate-500">Ville</p>
                      <p className="font-medium">{selectedCompany.city}</p>
                    </div>
                  )}
                </div>

                {/* OPCO */}
                {selectedCompany.opco && (
                  <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                    <h4 className="text-sm font-medium text-violet-700 dark:text-violet-300 mb-2">OPCO</h4>
                    <p className="font-medium text-violet-900 dark:text-violet-100">{selectedCompany.opco.name}</p>
                    {selectedCompany.opco.sectors && (
                      <p className="text-sm text-violet-600 dark:text-violet-400 mt-1">Secteurs : {selectedCompany.opco.sectors}</p>
                    )}
                  </div>
                )}

                {/* Contact tuteur */}
                {selectedCompany.contact_name && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Tuteur / Maître d'apprentissage</h4>
                    <p className="font-medium">{selectedCompany.contact_name}</p>
                    {selectedCompany.contact_position && (
                      <p className="text-sm text-slate-500">{selectedCompany.contact_position}</p>
                    )}
                    <div className="flex gap-4 mt-2">
                      {selectedCompany.contact_email && (
                        <span className="text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {selectedCompany.contact_email}
                        </span>
                      )}
                      {selectedCompany.contact_phone && (
                        <span className="text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedCompany.contact_phone}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Étudiants liés */}
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Étudiants rattachés ({selectedCompany.student_count})
                  </h4>
                  {selectedCompany.linked_students?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedCompany.linked_students.map(student => (
                        <div key={student.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <div>
                            <p className="font-medium">{student.full_name}</p>
                            <p className="text-xs text-slate-500">
                              {student.training_start_date && student.training_end_date 
                                ? `${student.training_start_date} - ${student.training_end_date}`
                                : 'Dates non définies'
                              }
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Aucun étudiant rattaché</p>
                  )}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                Fermer
              </Button>
              <Button onClick={() => { setShowViewModal(false); openEditModal(selectedCompany); }}>
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Link Students Modal */}
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-violet-600" />
                Rattacher des étudiants
              </DialogTitle>
              <DialogDescription>
                {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="max-h-96 overflow-y-auto space-y-2">
              {availableStudents.length === 0 ? (
                <p className="text-center py-4 text-slate-500">Aucun étudiant disponible</p>
              ) : (
                availableStudents.map(student => (
                  <div 
                    key={student.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      student.is_linked 
                        ? 'border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-900/20'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <div>
                      <p className="font-medium">{student.full_name}</p>
                      <p className="text-xs text-slate-500">{student.student_number || 'Sans numéro'}</p>
                    </div>
                    <Button
                      variant={student.is_linked ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleStudentLink(student)}
                    >
                      {student.is_linked ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          Rattaché
                        </>
                      ) : (
                        <>
                          <Link2 className="w-4 h-4 mr-1" />
                          Rattacher
                        </>
                      )}
                    </Button>
                  </div>
                ))
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Supprimer l'entreprise
              </DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer "{selectedCompany?.name}" ?
              </DialogDescription>
            </DialogHeader>
            
            {selectedCompany?.student_count > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>Attention :</strong> Cette entreprise a {selectedCompany.student_count} étudiant(s) rattaché(s). 
                  Elle sera désactivée au lieu d'être supprimée définitivement.
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                {saving ? (
                  <RefreshCcw className="w-4 h-4 animate-spin" />
                ) : (
                  selectedCompany?.student_count > 0 ? 'Désactiver' : 'Supprimer'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default CfaAdminCompaniesPage;
